/**
 * GarnishmentsTab — Saisies sur salaire / Tiers détenteurs.
 *
 * Fonctionnalités :
 *  - Liste des saisies (actives triées par priorité légale : pension → ATD → judiciaire → oppo)
 *  - Résumé mensuel actif (total à retenir sur bulletin)
 *  - Simulateur BOFiP (net annuel + pers charge → quotité saisissable)
 *  - Création via wizard IA (upload PDF acte huissier / ATD) ou manuelle
 *  - Consultation / édition / suppression / téléchargement PDF
 */
import { useEffect, useMemo, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Gavel, Sparkles, Plus, Upload, XCircle, Loader2, Download,
  Scale, Calculator, AlertTriangle, Trash2, CheckCircle2, ExternalLink,
  Wallet, Undo2, Play,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TYPE_LABEL = {
  pension_alimentaire: "Pension alimentaire",
  atd: "ATD Trésor Public",
  saisie_judiciaire: "Saisie judiciaire",
  opposition_administrative: "Opposition admin.",
  autre: "Autre",
};

const TYPE_BADGE = {
  pension_alimentaire: "bg-red-100 text-red-700 border-red-200",
  atd: "bg-amber-100 text-amber-700 border-amber-200",
  saisie_judiciaire: "bg-orange-100 text-orange-700 border-orange-200",
  opposition_administrative: "bg-yellow-100 text-yellow-800 border-yellow-200",
  autre: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_BADGE = {
  active: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Active" },
  paused: { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Suspendue" },
  finished: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Terminée" },
};

const fmtEUR = (v) =>
  v == null ? "—"
    : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// ─────────────────────────────────────────────────────────────
// Wizard IA (upload acte huissier / ATD)
// ─────────────────────────────────────────────────────────────

function AIExtractDialog({ open, onOpenChange, employeeId, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});

  const reset = () => { setFile(null); setResult(null); setForm({}); setDetecting(false); setApplying(false); };

  const runDetect = async () => {
    if (!file) { toast.error("Dépose le PDF de l'acte de saisie."); return; }
    setDetecting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/hr/employees/${employeeId}/garnishments/ai-extract`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      setForm({
        type: r.data.type || "autre",
        creditor: r.data.creditor || {},
        total_amount: r.data.amounts?.total_amount ?? null,
        monthly_amount: r.data.amounts?.monthly_amount ?? null,
        principal: r.data.amounts?.principal ?? null,
        interests: r.data.amounts?.interests ?? null,
        costs: r.data.amounts?.costs ?? null,
        start_date: r.data.start_date || "",
        end_date: r.data.end_date || "",
        notification_date: r.data.notification_date || "",
        cause: r.data.cause || "",
        status: "active",
      });
    } catch (e) { toast.error(apiError(e, "Détection IA échouée")); }
    finally { setDetecting(false); }
  };

  const apply = async () => {
    if (!form.type || !form.monthly_amount) {
      toast.error("Type et prélèvement mensuel obligatoires.");
      return;
    }
    setApplying(true);
    try {
      await api.post(`/hr/employees/${employeeId}/garnishments/ai-extract/apply`, {
        upload_id: result.upload_id,
        payload: form,
      });
      toast.success("Saisie enregistrée");
      onDone?.(); reset(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e, "Enregistrement échoué")); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                      data-testid="ai-garnishment-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Importer une saisie avec l'IA
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2 space-y-3">
          {!result && (
            <>
              <Alert className="bg-blue-50 border-blue-200">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-900">
                  Dépose l'acte d'huissier, l'ATD ou la décision de justice. L'IA extrait
                  <strong> type, créancier, IBAN, montants, référence, dates</strong>.
                </AlertDescription>
              </Alert>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) setFile(f); }}
                className={cn(
                  "border-2 border-dashed rounded-xl p-6 cursor-pointer transition",
                  file ? "border-emerald-400 bg-emerald-50/40"
                       : "border-slate-300 bg-slate-50/40 hover:border-blue-400 hover:bg-blue-50/40",
                )}
                data-testid="drop-garnishment"
              >
                <input id="garnishment-file-input" name="garnishment-file-input" ref={inputRef} type="file" className="hidden"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
                <div className="flex items-center gap-3">
                  {file
                    ? <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    : <Upload className="w-6 h-6 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      {file ? file.name : "Dépose ici le PDF"}
                    </p>
                    <p className="text-xs text-slate-500">
                      PDF, JPG, PNG — 8 Mo maximum
                    </p>
                  </div>
                  {file && (
                    <Button size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                      <XCircle className="w-4 h-4 text-slate-500" />
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Extraction · {result.source_filename}</span>
                <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
                  Confiance {Math.round((result.confidence || 0) * 100)}%
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Type de saisie *</Label>
                  <Select value={form.type || ""} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="garn-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABEL).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Créancier</Label>
                  <Input value={form.creditor?.name || ""}
                    onChange={(e) => setForm(p => ({ ...p, creditor: { ...(p.creditor || {}), name: e.target.value } }))}
                    placeholder="SCP Huissiers X, Trésor Public, Mme Y…" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase text-slate-500">IBAN destinataire</Label>
                  <Input value={form.creditor?.iban || ""} className="font-mono text-xs"
                    onChange={(e) => setForm(p => ({ ...p, creditor: { ...(p.creditor || {}), iban: e.target.value } }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Référence dossier</Label>
                  <Input value={form.creditor?.reference || ""}
                    onChange={(e) => setForm(p => ({ ...p, creditor: { ...(p.creditor || {}), reference: e.target.value } }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Date d'effet</Label>
                  <Input type="date" value={form.start_date || ""}
                    onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Montant total (€)</Label>
                  <Input type="number" step="0.01" value={form.total_amount ?? ""}
                    onChange={(e) => setForm(p => ({ ...p, total_amount: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Prélèvement mensuel (€) *</Label>
                  <Input type="number" step="0.01" value={form.monthly_amount ?? ""}
                    onChange={(e) => setForm(p => ({ ...p, monthly_amount: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                    data-testid="garn-monthly" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Motif</Label>
                  <Input value={form.cause || ""}
                    onChange={(e) => setForm(p => ({ ...p, cause: e.target.value }))} />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-slate-100">
          {!result && (
            <>
              <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Annuler</Button>
              <Button onClick={runDetect} disabled={!file || detecting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="wizard-garn-detect">
                {detecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Sparkles className="w-4 h-4 mr-2" />}
                Analyser avec l'IA
              </Button>
            </>
          )}
          {result && (
            <>
              <Button variant="outline" onClick={() => { setResult(null); setForm({}); }}>Retour</Button>
              <Button onClick={apply} disabled={applying}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-garn-apply">
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <Gavel className="w-4 h-4 mr-2" />}
                Enregistrer la saisie
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Dialog création manuelle
// ─────────────────────────────────────────────────────────────

function ManualDialog({ open, onOpenChange, employeeId, onDone }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: "pension_alimentaire",
    creditor: { name: "", iban: "", bic: "", reference: "" },
    monthly_amount: null, total_amount: null,
    start_date: "", cause: "", status: "active",
  });

  const submit = async () => {
    if (!form.type || !form.monthly_amount) {
      toast.error("Type et prélèvement mensuel obligatoires."); return;
    }
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/garnishments`, form);
      toast.success("Saisie créée");
      setForm({ type: "pension_alimentaire", creditor: {}, monthly_amount: null,
                total_amount: null, start_date: "", cause: "", status: "active" });
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Ajouter une saisie manuellement</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
              <SelectTrigger data-testid="manual-garn-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABEL).map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Créancier</Label>
              <Input value={form.creditor.name || ""}
                onChange={(e) => setForm(p => ({ ...p, creditor: { ...p.creditor, name: e.target.value } }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Référence</Label>
              <Input value={form.creditor.reference || ""}
                onChange={(e) => setForm(p => ({ ...p, creditor: { ...p.creditor, reference: e.target.value } }))} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase text-slate-500">IBAN destinataire</Label>
              <Input className="font-mono text-xs" value={form.creditor.iban || ""}
                onChange={(e) => setForm(p => ({ ...p, creditor: { ...p.creditor, iban: e.target.value } }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Mensuel (€) *</Label>
              <Input type="number" step="0.01" value={form.monthly_amount ?? ""}
                onChange={(e) => setForm(p => ({ ...p, monthly_amount: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                data-testid="manual-garn-monthly" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Total à recouvrer (€)</Label>
              <Input type="number" step="0.01" value={form.total_amount ?? ""}
                onChange={(e) => setForm(p => ({ ...p, total_amount: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date d'effet</Label>
              <Input type="date" value={form.start_date || ""}
                onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Suspendue</SelectItem>
                  <SelectItem value="finished">Terminée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Motif / cause</Label>
            <Textarea rows={2} value={form.cause || ""}
              onChange={(e) => setForm(p => ({ ...p, cause: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="manual-garn-save">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Simulateur BOFiP
// ─────────────────────────────────────────────────────────────

function QuotiteSimulator({ employeeId }) {
  const [netAnnuel, setNetAnnuel] = useState(30000);
  const [persCharge, setPersCharge] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const compute = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/hr/employees/${employeeId}/garnishments/quotite-preview`, {
        net_annuel: Number(netAnnuel) || 0,
        personnes_charge: Number(persCharge) || 0,
      });
      setResult(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { compute(); /* eslint-disable-next-line */ }, []);

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50"
          data-testid="quotite-simulator">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="w-4 h-4 text-purple-600" /> Simulateur quotité saisissable
          {result?.bareme_year && (
            <Badge variant="outline" className="ml-2 text-[10px] bg-white">
              Barème {result.bareme_year}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs uppercase text-slate-500">Net annuel (€)</Label>
            <Input type="number" step="100" value={netAnnuel}
              onChange={(e) => setNetAnnuel(e.target.value)}
              data-testid="sim-net-annuel" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Personnes à charge</Label>
            <Input type="number" min="0" value={persCharge}
              onChange={(e) => setPersCharge(e.target.value)}
              data-testid="sim-charge" />
          </div>
          <Button onClick={compute} disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="sim-compute">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <Calculator className="w-4 h-4 mr-2" />}
            Calculer
          </Button>
        </div>

        {result && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div className="p-3 bg-white rounded-lg border">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Saisissable mensuel</p>
              <p className="text-xl font-bold text-purple-700 mt-1" data-testid="sim-result-mensuel">
                {fmtEUR(result.saisissable_mensuel)}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">/ mois maximum</p>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Saisissable annuel</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{fmtEUR(result.saisissable_annuel)}</p>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Insaisissable / mois</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{fmtEUR(result.insaisissable_mensuel)}</p>
              <p className="text-[11px] text-slate-500 mt-1">SBI mini : {fmtEUR(result.sbi)}</p>
            </div>
          </div>
        )}

        {result?.note && (
          <p className="text-[11px] text-slate-500 pt-1">ⓘ {result.note}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Widget : Application au bulletin du mois (retenues auto)
// ─────────────────────────────────────────────────────────────

function DeductionsWidget({ employeeId, onDone }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hr/employees/${employeeId}/deductions/${period}`);
      setPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId, period]);

  const apply = async () => {
    if (!preview || preview.total_deductions <= 0) return;
    if (!window.confirm(
      `Appliquer ${fmtEUR(preview.total_deductions)} de retenues au bulletin ${period} ?\n\n` +
      `Cette action incrémentera paid_to_date sur chaque créancier et ` +
      `injectera la mutuelle dans les variables de paie du mois.`,
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/hr/employees/${employeeId}/deductions/${period}/apply`,
        { force_reapply: preview.applied });
      const finished = (r.data.updates || []).filter(u => u.finished).length;
      toast.success(`Retenues appliquées · ${r.data.updates.length} créancier(s)`
        + (finished > 0 ? ` · ${finished} saisie(s) soldée(s)` : ""));
      load(); onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    if (!window.confirm("Annuler l'application ? Les paid_to_date seront décrémentés et les saisies auto-soldées réactivées.")) return;
    setBusy(true);
    try {
      await api.post(`/hr/employees/${employeeId}/deductions/${period}/revert`);
      toast.success("Application annulée");
      load(); onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50/50 to-white"
          data-testid="deductions-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="w-4 h-4 text-purple-600" />
          Application au bulletin du mois
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center">
          <div>
            <Label className="text-xs uppercase text-slate-500">Période</Label>
            <Input type="month" value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-40" data-testid="deductions-period" />
          </div>
          {loading ? (
            <div className="p-2"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : preview && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-white rounded border">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Saisies</p>
                <p className="text-lg font-bold text-slate-900" data-testid="preview-garn">
                  {fmtEUR(preview.garnishments_total)}
                </p>
              </div>
              <div className="p-2 bg-white rounded border">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Mutuelle</p>
                <p className="text-lg font-bold text-pink-700" data-testid="preview-mut">
                  {fmtEUR(preview.mutuelle_amount)}
                </p>
              </div>
              <div className="p-2 bg-purple-100/60 rounded border border-purple-200">
                <p className="text-[10px] uppercase tracking-wider text-purple-700">Total à retenir</p>
                <p className="text-lg font-bold text-purple-700" data-testid="preview-total">
                  {fmtEUR(preview.total_deductions)}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            {preview?.applied ? (
              <>
                <Badge variant="outline"
                  className="bg-emerald-50 text-emerald-700 border-emerald-200 justify-center"
                  data-testid="applied-badge">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Appliqué
                </Badge>
                <Button size="sm" variant="outline"
                  onClick={revert} disabled={busy}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="revert-deductions">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                  Annuler
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={apply}
                disabled={busy || !preview || preview.total_deductions <= 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="apply-deductions">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      : <Play className="w-3.5 h-3.5 mr-1" />}
                Appliquer
              </Button>
            )}
          </div>
        </div>

        {preview?.applied && preview?.applied_at && (
          <p className="text-[11px] text-emerald-700">
            <CheckCircle2 className="w-3 h-3 inline mr-1" />
            Appliqué le {new Date(preview.applied_at).toLocaleString("fr-FR")}
            {preview.applied_by_email && <> par {preview.applied_by_email}</>}
          </p>
        )}

        {preview?.garnishments?.length > 0 && (
          <div className="pt-2 border-t border-slate-100 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
              Détail par créancier
            </p>
            {preview.garnishments.map((g, i) => (
              <div key={g.garnishment_id}
                    className="flex items-center justify-between text-xs p-1.5 bg-slate-50 rounded">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Badge variant="outline" className={cn("text-[10px]", TYPE_BADGE[g.type])}>
                    {TYPE_LABEL[g.type]}
                  </Badge>
                  <span className="truncate text-slate-700">
                    {g.creditor_name || "—"}
                    {g.reference && <span className="text-slate-500 ml-1">· {g.reference}</span>}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <span className="font-medium">{fmtEUR(g.monthly_effective)}</span>
                  {g.total_amount != null && (
                    <span className="text-[10px] text-slate-500">
                      {fmtEUR(g.paid_to_date_before)} / {fmtEUR(g.total_amount)}
                    </span>
                  )}
                  {g.will_finish && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                      Soldera
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────
// Vue principale
// ─────────────────────────────────────────────────────────────

export default function GarnishmentsTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get(`/hr/employees/${employeeId}/garnishments`),
        api.get(`/hr/employees/${employeeId}/garnishments/active-summary`),
      ]);
      setRows(listRes.data.garnishments || []);
      setSummary(sumRes.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const remove = async (gid) => {
    if (!window.confirm("Supprimer cette saisie ?")) return;
    try {
      await api.delete(`/hr/garnishments/${gid}`);
      toast.success("Saisie supprimée"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (gid, status) => {
    try {
      await api.patch(`/hr/garnishments/${gid}`, { status });
      toast.success("Statut mis à jour"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activeCount = summary?.active_count || 0;
  const monthlyTotal = summary?.monthly_total || 0;

  return (
    <div className="space-y-4" data-testid="garnishments-tab">
      {/* Header actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Gavel className="w-5 h-5 text-slate-700" />
            Saisies sur salaire
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Pension alimentaire, ATD, saisies judiciaires — ordonnées par priorité légale
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={() => setAiOpen(true)} variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-garnishment-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Importer avec IA
            </Button>
            <Button onClick={() => setManualOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="manual-garnishment-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Ajouter manuellement
            </Button>
          </div>
        )}
      </div>

      {/* Résumé mensuel actif */}
      {activeCount > 0 && (
        <Card className={cn(
          "border-l-4",
          monthlyTotal > 500 ? "border-l-red-500 bg-red-50/40" : "border-l-amber-500 bg-amber-50/40",
        )}>
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                  Total à retenir sur le bulletin
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1"
                    data-testid="monthly-total">{fmtEUR(monthlyTotal)}</p>
                <p className="text-xs text-slate-600 mt-1">
                  {activeCount} saisie{activeCount > 1 ? "s" : ""} active{activeCount > 1 ? "s" : ""}
                  {" — priorité légale : pension alimentaire › ATD › judiciaire › opposition"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {Object.entries(summary?.breakdown || {}).map(([t, v]) => (
                  <Badge key={t} variant="outline" className={TYPE_BADGE[t] || TYPE_BADGE.autre}>
                    {v.label} · {fmtEUR(v.monthly_total)}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Simulateur BOFiP */}
      <QuotiteSimulator employeeId={employeeId} />

      {/* Application au bulletin du mois */}
      <DeductionsWidget employeeId={employeeId} onDone={load} />

      {/* Liste des saisies */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Détail des saisies</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-100 animate-pulse rounded" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <Gavel className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">
                Aucune saisie enregistrée pour ce salarié.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {canWrite && "Importe le PDF d'un acte d'huissier ou d'un ATD pour commencer."}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((g, idx) => (
                <div key={g.id} className="p-4 hover:bg-slate-50 transition"
                      data-testid={`garnishment-row-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-start">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline"
                        className={cn("text-[10px] font-semibold", TYPE_BADGE[g.type])}>
                        #{idx + 1}
                      </Badge>
                      <Badge variant="outline" className={TYPE_BADGE[g.type]}>
                        {TYPE_LABEL[g.type]}
                      </Badge>
                      <Badge variant="outline"
                        className={(STATUS_BADGE[g.status] || STATUS_BADGE.active).cls}>
                        {(STATUS_BADGE[g.status] || STATUS_BADGE.active).label}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {g.creditor?.name || "Créancier inconnu"}
                        {g.creditor?.reference && (
                          <span className="text-xs text-slate-500 ml-2">
                            · Réf. {g.creditor.reference}
                          </span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-1">
                        <span><strong>{fmtEUR(g.monthly_amount)}</strong>/mois</span>
                        {g.total_amount != null && <span>Total : {fmtEUR(g.total_amount)}</span>}
                        {g.paid_to_date != null && <span>Réglé : {fmtEUR(g.paid_to_date)}</span>}
                        {g.start_date && <span>Depuis : {new Date(g.start_date).toLocaleDateString("fr-FR")}</span>}
                        {g.creditor?.iban && (
                          <span className="font-mono text-[10px]">
                            IBAN : {g.creditor.iban.slice(0, 4)}…{g.creditor.iban.slice(-4)}
                          </span>
                        )}
                      </div>
                      {g.cause && (
                        <p className="text-[11px] text-slate-500 mt-1 italic">{g.cause}</p>
                      )}
                    </div>
                    {canWrite && (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {g.file_asset_id && (
                          <a href={`/api/hr/garnishments/${g.id}/file`}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100">
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <Select value={g.status || "active"}
                          onValueChange={(v) => setStatus(g.id, v)}>
                          <SelectTrigger className="h-8 text-xs w-32"
                            data-testid={`garn-status-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Suspendue</SelectItem>
                            <SelectItem value="finished">Terminée</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost"
                          onClick={() => remove(g.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`garn-delete-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AIExtractDialog open={aiOpen} onOpenChange={setAiOpen}
        employeeId={employeeId} onDone={load} />
      <ManualDialog open={manualOpen} onOpenChange={setManualOpen}
        employeeId={employeeId} onDone={load} />
    </div>
  );
}
